import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from '../context/ModalContext';
import { adminService } from '../services/adminService';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-CA') + ' ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const styles = {
  page: {
    padding: '0',
  },
  card: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  countLabel: {
    fontSize: '14px',
    color: '#6b7280',
  },
  searchInput: {
    padding: '8px 14px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d9e0ea',
    borderRadius: '6px',
    fontSize: '14px',
    width: '250px',
    outline: 'none',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '1px solid #d9e0ea',
    color: '#475569',
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#1f2937',
    fontSize: '14px',
  },
  tdSubtle: {
    color: '#6b7280',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  restoreBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#6b7280',
    background: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '15px',
  },
  loadingState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#6b7280',
    fontSize: '15px',
  },
};

export default function TrashPage() {
  const { showAlert, showConfirm } = useModal();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getTrash();
      setForms(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load trash:', error);
      await showAlert('Failed to load trash', 'Error');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const filteredForms = useMemo(() => {
    if (!search.trim()) return forms;
    const q = search.toLowerCase();
    return forms.filter(f =>
      (f.name || '').toLowerCase().includes(q) ||
      (f.id || '').toLowerCase().includes(q)
    );
  }, [forms, search]);

  const handleRestore = async (formId, formName) => {
    const ok = await showConfirm(`Restore "${formName}" from trash?`, {
      title: 'Restore Form',
    });
    if (!ok) return;
    try {
      await adminService.restoreForm(formId);
      await loadTrash();
    } catch (error) {
      console.error('Restore failed:', error);
      await showAlert('Failed to restore form', 'Error');
    }
  };

  const handlePermanentDelete = async (formId, formName) => {
    const ok = await showConfirm(`Permanently delete "${formName}"? This cannot be undone.`, {
      title: 'Permanent Delete',
      danger: true,
      okText: 'Delete Forever',
    });
    if (!ok) return;
    try {
      await adminService.permanentDeleteForm(formId);
      await loadTrash();
    } catch (error) {
      console.error('Permanent delete failed:', error);
      await showAlert('Failed to permanently delete form', 'Error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.sectionTitle}>Trash</h2>
            <span style={styles.countLabel}>{filteredForms.length} deleted form{filteredForms.length !== 1 ? 's' : ''}</span>
          </div>
          <input
            type="text"
            placeholder="Search deleted forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {loading && <div style={styles.loadingState}>Loading...</div>}

        {!loading && filteredForms.length === 0 && (
          <div style={styles.emptyState}>Trash is empty.</div>
        )}

        {!loading && filteredForms.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Template Name</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Deleted At</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredForms.map((form) => (
                  <tr key={form.id}>
                    <td style={styles.td}>{form.name || form.id}</td>
                    <td style={{ ...styles.td, ...styles.tdSubtle }}>
                      {(form.status || 'draft').charAt(0).toUpperCase() + (form.status || 'draft').slice(1)}
                    </td>
                    <td style={{ ...styles.td, ...styles.tdSubtle }}>
                      {formatDate(form.deletedAt || form.updatedAt)}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          type="button"
                          style={styles.restoreBtn}
                          onClick={() => handleRestore(form.id, form.name)}
                        >
                          Restore
                        </button>
                        <button
                          type="button"
                          style={styles.deleteBtn}
                          onClick={() => handlePermanentDelete(form.id, form.name)}
                        >
                          Delete Forever
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
